
import { useEffect } from "react";
import { format, isValid, parseISO } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getPreviewUrl } from "@/utils/imageUtils";

interface CertificatePrintPreviewProps {
  print: any;
  certificate: {
    hologram_number: string;
    issue_date: string;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Define a proper type for the artwork preview settings
interface ArtworkPreviewSettings {
  width: string;
  alignment: "left" | "center" | "right";
}

export function CertificatePrintPreview({ 
  print, 
  certificate,
  open, 
  onOpenChange 
}: CertificatePrintPreviewProps) {
  // First fetch the creator's details to check for a custom template
  const { data: creatorDetails } = useQuery({
    queryKey: ["creator-details", print?.editions?.artworks?.creator_id],
    queryFn: async () => {
      if (!print?.editions?.artworks?.creator_id) return null;
      
      const { data, error } = await supabase
        .from("creators")
        .select("certificate_template")
        .eq("creator_id", print.editions.artworks.creator_id)
        .single();

      if (error) {
        console.error("Error fetching creator details:", error);
        throw error;
      }

      console.log("Fetched creator details:", data);
      return data;
    },
    enabled: !!print?.editions?.artworks?.creator_id,
  });

  // Then fetch the appropriate template (creator's custom template or default)
  const { data: template } = useQuery({
    queryKey: ["certificate-template", creatorDetails?.certificate_template],
    queryFn: async () => {
      console.log("Fetching certificate template");
      let query = supabase
        .from("templates")
        .select("*")
        .eq("type", "certificate");

      // If creator has a specific template, use that
      if (creatorDetails?.certificate_template) {
        query = query.eq("template_id", creatorDetails.certificate_template);
      } else {
        // Otherwise use the default template
        query = query.eq("is_default", true);
      }

      const { data, error } = await query.single();

      if (error) {
        console.error("Error fetching template:", error);
        throw error;
      }

      console.log("Fetched template:", data);
      return data;
    },
    enabled: !!print?.editions?.artworks?.creator_id,
  });

  // Fetch full provider details
  const { data: providerDetails } = useQuery({
    queryKey: ["provider-details", print?.print_service_providers?.name],
    queryFn: async () => {
      if (!print?.print_service_providers?.name) return null;
      
      const { data, error } = await supabase
        .from("print_service_providers")
        .select("name, city, country")
        .eq("name", print.print_service_providers.name)
        .single();

      if (error) {
        console.error("Error fetching provider details:", error);
        throw error;
      }

      console.log("Fetched provider details:", data);
      return data;
    },
    enabled: !!print?.print_service_providers?.name,
  });

  useEffect(() => {
    console.log("CertificatePrintPreview useEffect triggered:", {
      open,
      template,
      print,
      certificate,
      providerDetails
    });

    if (open && template) {
      console.log("Preparing to open print window with data:", {
        template,
        print,
        certificate,
        providerDetails
      });

      // Create window title
      const windowTitle = `${print.editions?.artworks?.title || 'Untitled'} | ${print.editions?.edition_name || 'No Edition'} | #${print.edition_number}`;
      
      // Get preview URL for the artwork
      const artworkPreviewUrl = print.editions?.artworks?.preview 
        ? getPreviewUrl(print.editions.artworks.preview)
        : '';
        
      console.log("Artwork preview URL:", artworkPreviewUrl);
      
      // Format the provider information including name, city, and country
      const formattedProviderInfo = providerDetails ? 
        `${providerDetails.name}, ${providerDetails.city}, ${providerDetails.country}` : 
        print.print_service_providers?.name || '';
      
      // Define base template variables with their values
      const baseTemplateVars = {
        artist_name: getArtistName(print),
        artwork_title: print.editions?.artworks?.title,
        creation_date: print.editions?.artworks?.creation_date,
        art_technique: print.editions?.artworks?.art_technique,
        width: print.editions?.width,
        height: print.editions?.height,
        issue_date: formatDate(certificate.issue_date),
        print_number: `${print.edition_number}${print.editions?.edition_limit ? ` / ${print.editions.edition_limit}` : ''}`,
        media: print.media?.media_name,
        print_provider: formattedProviderInfo,
        hologram_number: certificate.hologram_number,
        artwork_preview: artworkPreviewUrl,
      };

      // Get the variable format configuration from the template, or use an empty object as default
      const variableFormat = template.variable_format || {};
      
      // Parse the artwork preview settings from the template, or use defaults
      let artworkPreviewSettings: ArtworkPreviewSettings = { 
        width: '300', 
        alignment: 'center' 
      };
      
      if (template.artwork_preview_settings) {
        try {
          // Handle different possible types for artwork_preview_settings
          if (typeof template.artwork_preview_settings === 'string') {
            const parsedSettings = JSON.parse(template.artwork_preview_settings);
            artworkPreviewSettings = {
              width: parsedSettings.width || '300',
              alignment: (parsedSettings.alignment as "left" | "center" | "right") || 'center'
            };
          } else if (typeof template.artwork_preview_settings === 'object') {
            const settings = template.artwork_preview_settings as any;
            artworkPreviewSettings = {
              width: settings.width || '300',
              alignment: (settings.alignment as "left" | "center" | "right") || 'center'
            };
          }
        } catch (e) {
          console.error("Error parsing artwork preview settings:", e);
        }
      }

      console.log("Template variables prepared:", baseTemplateVars);
      console.log("Variable format configuration:", variableFormat);
      console.log("Artwork preview settings:", artworkPreviewSettings);

      // Replace template variables in the fetched template
      let content = template.content;
      Object.entries(baseTemplateVars).forEach(([key, value]) => {
        if (value != null && value !== '') {
          // Special handling for artwork_preview - insert an img tag with custom styling
          if (key === 'artwork_preview') {
            const imgTag = `<div class="artwork-preview-container" style="text-align: ${artworkPreviewSettings.alignment}">
              <img src="${value}" alt="Artwork Preview" class="artwork-preview" style="max-width: ${artworkPreviewSettings.width}px;" />
            </div>`;
            content = content.replace(new RegExp(`{${key}}`, 'g'), imgTag);
          } else {
            const format = variableFormat[key] || { prefix: "", suffix: "" };
            const prefix = format.prefix || "";
            const suffix = format.suffix || "";
            const replacement = `${prefix}${value}${suffix}`;
            content = content.replace(new RegExp(`{${key}}`, 'g'), replacement);
          }
        } else {
          // If value is null or empty, remove the placeholder entirely
          content = content.replace(new RegExp(`{${key}}`, 'g'), '');
        }
      });

      // Convert markdown-like syntax to HTML
      content = content
        .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold text-center mb-6">$1</h1>')
        .replace(/^## (.*$)/gm, '<h2 class="text-xl font-semibold mb-4">$1</h2>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/^---$/gm, '<hr class="my-6">')
        .replace(/\n/g, '<br>');

      // Create the HTML document
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${windowTitle}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                max-width: 800px;
                margin: 20px auto;
                padding: 20px;
                line-height: 1.6;
              }
              .certificate {
                border: 2px solid #000;
                padding: 40px;
                margin-bottom: 20px;
              }
              hr {
                border: none;
                border-top: 1px solid #000;
                margin: 20px 0;
              }
              .artwork-preview-container {
                margin: 20px 0;
              }
              .artwork-preview {
                max-height: 300px;
                display: inline-block;
                object-fit: contain;
              }
              @media print {
                body {
                  margin: 0;
                  padding: 20px;
                }
                .no-print {
                  display: none;
                }
                .certificate {
                  border: none;
                }
                .artwork-preview {
                  max-height: 250px;
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="certificate">
              ${content}
            </div>
            <div class="no-print" style="text-align: center; margin-top: 20px;">
              <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">
                Print Certificate
              </button>
            </div>
          </body>
        </html>
      `;

      console.log("Opening print window with HTML content");
      
      // Open new window and write content
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
      } else {
        console.error("Failed to open print window");
      }

      // Close the modal
      onOpenChange(false);
    }
  }, [open, print, certificate, template, providerDetails]);

  // Helper functions
  const getArtistName = (print: any) => {
    const creator = print.editions?.artworks?.creators;
    if (!creator) return null;
    return creator.nick_name || `${creator.name} ${creator.surname}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return null;
    const date = parseISO(dateString);
    return isValid(date) ? format(date, 'd MMMM yyyy') : null;
  };

  // Component doesn't render anything in the main window
  return null;
}
